/**
 * Easter recipe cards — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const easterRecipeCardsPack: ToilePack = {
    slug: 'easter-recipe-cards',
    category: 'season',
    name: 'Easter recipe cards',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'buns', label: 'Hot cross buns', subject: 'A vertical recipe card lettered "Hot Cross Buns" with "Ingredients" and "Method" headers and blank lines, a bun motif'},
        {key: 'roast', label: 'Roast lamb', subject: 'A vertical recipe card lettered "Easter Roast" with blank lines and a rosemary motif'},
        {key: 'family', label: 'Family recipe', subject: 'A vertical recipe card lettered "From the kitchen of" with a blank name line, headers and blank lines, a tulip border'},
    ],
    styles: [
        {key: 'pastel', label: 'Pastel', style: 'pastel easter printable, egg and floral borders, soft rounded lettering, print-ready', palette: 'Soft pink, mint, lilac and butter yellow', framing: 'Laid out on a vertical page with an even white margin'},
        {key: 'vintage', label: 'Vintage', style: 'vintage easter printable, engraved floral borders, elegant serif lettering, print-ready', palette: 'Rose, sage, cream and a little gold', framing: 'Laid out on a cream page with an ornate border and a margin'},
        {key: 'kids', label: 'Kids', style: "kids' easter printable, bright cartoon bunnies and chicks, rounded lettering, print-ready", palette: 'Bright yellow, sky blue, grass green and pink', framing: 'Laid out on a vertical page with a playful border and a white margin'},
    ],
    directions: ['for a slow-cooker version instead', 'with a metric measurements column', 'as a small gift-tag size'],
    directionHint: 'try: a metric measurements column',
    seo: {
        title: 'Easter Recipe Cards: Buns, Roast & Family Recipe',
        metaDescription: "Nine Easter recipe cards — hot cross buns, roast lamb, and a blank family recipe — with ruled lines to fill in, in pastel, vintage, and kids' styles.",
        h1: 'Easter recipe cards: hot cross buns, roast, family recipe',
        tagline: 'Nine cards, three recipes, three styles each — headers lettered, lines left blank.',
        intro: "Three Easter recipe cards — a hot-cross-bun card, a roast-lamb card, and a blank family-recipe card — each with the headers lettered and ruled lines left blank to write in, in pastel, vintage, and kids' cartoon styles. Ask for a different dish, a bigger card, or a recipe-box size, and the same card grammar carries it.",
        useCases: [
            'a family recipe box',
            'an Easter dinner planning card',
            'a handwritten recipe gift',
            'a classroom cooking activity',
        ],
        faq: [
            {q: 'Are the recipe steps already written in?', a: "No — 'Ingredients' and 'Method' headers print lettered, and every line beneath stays blank for you to write in."},
            {q: 'What size do these print at?', a: 'Each card is a single vertical page; trim it down to a standard recipe-box size after printing if you like.'},
            {q: 'Can I get a different recipe or card size?', a: 'Yes — the direction field takes any variation, including a different dish or a resized card.'},
            {q: 'What are the three styles?', a: "Pastel, vintage engraved-border, and bright kids' cartoon."},
        ],
    },
}
